import { config } from '@euler/config';
import { TokenService } from '@euler/lib/services';
import { AppState, AppStateStatus } from 'react-native';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import invariant from 'tiny-invariant';
import { ChatEvent, ChatEventKind } from './chatEvents';

/**
 * Refresh-token hook passed into the client (rather than imported) so this
 * module needn't pull in the auth service / HTTP client — which would
 * create a circular dep with services/factory. Returns the new access
 * token, or null if refresh failed / no refresh-token available.
 */
export type ChatWsTokenRefresher = () => Promise<string | null>;

/** Refresh the access token if it expires within this window. */
const TOKEN_REFRESH_WINDOW_MS = 60_000;
/**
 * Hard cap on how long we wait for the WS handshake to complete before
 * force-closing and rescheduling. Browsers don't bound this on their
 * own — without the cap, a stalled handshake leaves the client in
 * `connecting` forever and no reconnect is scheduled.
 */
const CONNECT_TIMEOUT_MS = 15_000;

export type ChatConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

/**
 * Why the gateway refused this store for good: retrying cannot help until
 * the store's service or its chat is switched back on.
 */
export type ChatRefusal =
  | 'store_service_finished'
  | 'store_feature_not_enabled';

const kFinalRefusals = new Set<string>([
  'store_service_finished',
  'store_feature_not_enabled',
]);

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000, 30000];
const HEARTBEAT_INTERVAL_MS = 20_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;

/** Platform timer handle — a number on web, an object on native. */
type Timer = ReturnType<typeof setTimeout>;

function withJitter(ms: number): number {
  // Equal jitter: spread retries across [ms/2, ms) instead of clustering
  // near the base, so a fleet reconnecting after a gateway restart doesn't
  // thundering-herd. Floor at 500ms.
  const half = ms / 2;
  return Math.max(500, Math.floor(half + Math.random() * half));
}

export class ChatWSClient {
  readonly connectionState$ = new BehaviorSubject<ChatConnectionState>(
    'disconnected',
  );
  /**
   * Set when the gateway refuses the store for good; stops reconnecting
   * until the app next comes to the foreground.
   */
  readonly refusal$ = new BehaviorSubject<ChatRefusal | null>(null);

  private ws: WebSocket | null = null;
  private reconnectTimer: Timer | null = null;
  private connectTimeoutTimer: Timer | null = null;
  private heartbeatTimer: Timer | null = null;
  private heartbeatTimeoutTimer: Timer | null = null;
  /** Fires shortly before the auth token expires to send `auth.refresh`. */
  private authRefreshTimer: Timer | null = null;
  private reconnectAttempt = 0;
  /**
   * Number of live `connect()` holds. Several surfaces (inbox, a floating
   * chat room, …) can want the transport at once; the socket stays up
   * while this is > 0 (see `alive`) and the last `disconnect()` tears it
   * down. connect/disconnect are acquire/release and must be balanced.
   */
  private refCount = 0;
  /** Last observed app foreground state. */
  private appActive = true;
  /** Guards against overlapping connect attempts across the token fetch. */
  private connecting = false;
  /** Token to present in the `auth` frame on open; set before each connect. */
  private authToken: string | null = null;
  private appStateSub: { remove: () => void } | null = null;

  private readonly events$ = new Subject<ChatEvent>();

  constructor(
    private readonly tokenService: TokenService,
    private readonly tokenRefresher: ChatWsTokenRefresher,
  ) {
    // Bind the socket event handlers once so the same stable reference can
    // be attached to / detached from each socket (see
    // tearDownWebsocketConnection). Stable references are what let us
    // detach a replaced socket's handlers, so no "is this still the
    // current socket?" guard is ever needed.
    this.onSocketOpen = this.onSocketOpen.bind(this);
    this.onSocketMessage = this.onSocketMessage.bind(this);
    this.onSocketClose = this.onSocketClose.bind(this);
    this.onSocketError = this.onSocketError.bind(this);
    this.onAppStateChange = this.onAppStateChange.bind(this);

    try {
      this.appStateSub = AppState.addEventListener(
        'change',
        this.onAppStateChange,
      );
    } catch {
      // AppState may not be available in non-RN test envs — that's fine.
    }
  }

  get isConnected(): boolean {
    return this.connectionState$.getValue() === 'connected';
  }

  /** Whether any surface still holds a `connect()` reference. */
  private get alive(): boolean {
    return this.refCount > 0;
  }

  /**
   * Acquire a reference to the live transport. The socket opens on the
   * first hold and is reused by later ones; it stays up until the last
   * `disconnect()` releases it. A reference taken while backgrounded
   * connects on the next foreground. Pair every `connect()` with exactly
   * one `disconnect()`.
   */
  async connect(): Promise<void> {
    this.refCount++;
    if (!this.appActive) return; // wait until foreground
    await this.doConnect();
  }

  /** Release a reference; tears the socket down once the last one is gone. */
  disconnect(): void {
    if (this.refCount > 0) this.refCount--;
    if (this.alive) return; // other holders still want the transport
    this.tearDownWebsocketConnection();
  }

  /**
   * Tear down the client for good — drops all references, closes the
   * socket, and removes the AppState listener. Called when the owning
   * session provider unmounts (account switch / logout) so a stale
   * instance doesn't keep reacting to foreground/background changes.
   */
  dispose(): void {
    this.refCount = 0;
    this.tearDownWebsocketConnection();
    this.appStateSub?.remove();
    this.appStateSub = null;
  }

  sendTyping(roomId: string, stopped: boolean = false): void {
    this.send({ type: 'chat.typing', data: { roomId, stopped } });
  }

  sendPresence(state: 'online' | 'offline'): void {
    this.send({ type: 'chat.presence', data: { state } });
  }

  onEvent(): Observable<ChatEvent> {
    return this.events$.asObservable();
  }

  onRoomEvent(roomId: string): Observable<ChatEvent> {
    return this.events$.pipe(
      filter(e => e.kind !== 'presence' && e.roomId === roomId),
    );
  }

  onKind<K extends ChatEventKind>(
    kind: K,
  ): Observable<Extract<ChatEvent, { kind: K }>> {
    return this.events$.pipe(
      filter((e): e is Extract<ChatEvent, { kind: K }> => e.kind === kind),
    );
  }

  // ─────────────────────────────────────────── connection lifecycle

  /**
   * Drive a connection attempt: dedupe overlapping attempts (across the
   * async token fetch) and handle any failure by scheduling a reconnect.
   * The actual socket setup lives in `initWebSocketConnection`.
   */
  private async doConnect(): Promise<void> {
    if (this.connecting || this.refusal$.value != null) return;
    this.connecting = true;
    try {
      await this.initWebSocketConnection();
    } catch (e) {
      console.error('[chat-ws] connect failed', e);
      this.scheduleReconnect();
    } finally {
      this.connecting = false;
    }
  }

  /**
   * Open (or reuse) THE connection. The live socket is always the one on
   * `this.ws`: a connected socket is kept; otherwise any prior socket is
   * cleaned up (handlers detached, closed) before a fresh one is created
   * and wired up. Throws on failure (no token, ctor error); `doConnect`
   * handles it.
   */
  private async initWebSocketConnection(): Promise<void> {
    if (this.ws != null && this.isConnected) return;

    const token = await this.getFreshToken();
    invariant(token, 'no auth token available');
    this.authToken = token;

    // Re-check after the async token fetch: a rapid disconnect (refCount
    // → 0) or a background transition during the await means we no longer
    // want a socket — bail rather than opening a zombie one.
    if (!this.alive || !this.appActive) return;

    // Detach + close any prior socket before opening a new one.
    this.tearDownWebsocketConnection();

    this.connectionState$.next(
      this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting',
    );

    this.ws = new WebSocket(config.chatEndpoint);
    this.ws.addEventListener('open', this.onSocketOpen);
    this.ws.addEventListener('message', this.onSocketMessage);
    this.ws.addEventListener('close', this.onSocketClose);
    this.ws.addEventListener('error', this.onSocketError);

    this.armConnectTimeout();
  }

  /**
   * Idempotent teardown of the live connection (paired with
   * `initWebSocketConnection`): stop every timer, detach + close the
   * socket, and reset the state to `disconnected`. Detaching the handlers
   * BEFORE closing means the close we trigger here never re-enters
   * `onSocketClose` — so an intentional teardown (disconnect / dispose /
   * background / replace) doesn't kick off a reconnect. Safe to call
   * repeatedly, and from within `onSocketClose`.
   *
   * Callers that want a non-`disconnected` follow-up state (a reconnect or
   * a fresh connect) set it right after. Connection-state handling will be
   * centralized later.
   */
  private tearDownWebsocketConnection(): void {
    this.clearConnectTimeout();
    this.stopHeartbeat();
    this.clearAuthRefreshTimer();
    this.clearReconnectTimer();
    if (this.ws != null) {
      this.ws.removeEventListener('open', this.onSocketOpen);
      this.ws.removeEventListener('message', this.onSocketMessage);
      this.ws.removeEventListener('close', this.onSocketClose);
      this.ws.removeEventListener('error', this.onSocketError);
      try {
        this.ws.close();
      } catch {
        // ignore — we've already detached; nothing depends on the close.
      }
      this.ws = null;
    }
    this.connectionState$.next('disconnected');
  }

  private onSocketOpen(): void {
    invariant(this.ws, 'onSocketOpen fired without a socket');
    this.clearConnectTimeout();
    // Protocol-layer auth: the `auth` frame is the first thing we send.
    // The gateway drops every other frame until it acks (`auth.ack`), so
    // the heartbeat is deferred until then — see handleFrame.
    if (this.authToken != null) {
      this.send({ type: 'auth', data: { token: this.authToken } });
    }
  }

  private onSocketMessage(event: MessageEvent): void {
    invariant(this.ws, 'onSocketMessage fired without a socket');
    try {
      const frame = JSON.parse(event.data as string);
      this.handleFrame(frame);
    } catch (e) {
      console.error('[chat-ws] failed to parse frame', e);
    }
  }

  private onSocketClose(): void {
    invariant(this.ws, 'onSocketClose fired without a socket');
    this.scheduleReconnect();
  }

  private onSocketError(event: Event): void {
    console.error('[chat-ws] error', event);
  }

  private onAppStateChange(state: AppStateStatus): void {
    const wasActive = this.appActive;
    this.appActive = state === 'active';
    if (this.appActive && !wasActive) {
      // One more try per foreground: chat may have been switched back on.
      if (this.refusal$.value != null) this.refusal$.next(null);
      if (this.alive && !this.isConnected) {
        void this.doConnect();
      }
    } else if (!this.appActive && wasActive) {
      // Background — drop the socket without changing intent. Teardown
      // detaches the handlers (so its close won't reconnect) and resets the
      // state to 'disconnected'; we re-open on the next foreground.
      this.tearDownWebsocketConnection();
    }
  }

  // ─────────────────────────────────────────── auth / token

  /**
   * Current access token, proactively refreshed if it's within the
   * refresh window of expiry. Without this, auth after the JWT lifetime
   * (~10 min) would be rejected by the gateway and loop forever — the
   * "stalled for a long time, no reconnect" symptom. Used for both the
   * initial `auth` frame and the periodic `auth.refresh`.
   */
  private async getFreshToken(): Promise<string | null> {
    const token = await this.tokenService.getToken();
    const exp = await this.tokenService.getTokenExpiry();
    const expiringSoon =
      exp != null && exp * 1000 - Date.now() < TOKEN_REFRESH_WINDOW_MS;
    if (token && expiringSoon) {
      // Inside the refresh window a brand-new token is required to stay
      // ahead of expiry. Return the refreshed one, or null if refresh
      // failed — NOT the stale token, which would just get rejected and
      // busy-loop the auth.refresh / reconnect path.
      try {
        return await this.tokenRefresher();
      } catch (e) {
        console.error('[chat-ws] token refresh failed', e);
        return null;
      }
    }
    return token ?? null;
  }

  // ─────────────────────────────────────────── frames

  private handleFrame(frame: { type: string; data?: any }): void {
    switch (frame.type) {
      case 'auth.ack':
        if (frame.data?.ok) {
          // Authenticated AND subscribed — the gateway subscribes the
          // per-user channel during auth, so `auth.ack{ok}` is the
          // "connection healthy" signal. Clear the reconnect backoff here
          // (not at bare open, which isn't authenticated yet), arm the
          // pre-expiry refresh, and start the heartbeat now that the
          // gateway accepts app frames.
          this.reconnectAttempt = 0;
          this.connectionState$.next('connected');
          this.scheduleAuthRefresh(frame.data.tokenExp);
          this.startHeartbeat();
        } else if (kFinalRefusals.has(frame.data?.subCode)) {
          this.refuse(frame.data.subCode as ChatRefusal);
        } else {
          // Token rejected (expired / invalid). Drop and reconnect; the
          // next attempt fetches a fresh token via getFreshToken.
          console.error('[chat-ws] auth rejected:', frame.data?.error);
          this.scheduleReconnect();
        }
        break;
      case 'chat.event':
        if (frame.data) this.events$.next(frame.data as ChatEvent);
        break;
      case 'auth.refresh.ack':
        if (frame.data?.ok) {
          this.scheduleAuthRefresh(frame.data.tokenExp);
        } else if (kFinalRefusals.has(frame.data?.subCode)) {
          this.refuse(frame.data.subCode as ChatRefusal);
        } else {
          // Refresh rejected — the gateway will terminate at the original
          // expiry. Reconnect now to re-auth with a fresh token.
          console.error('[chat-ws] auth.refresh rejected:', frame.data?.error);
          this.scheduleReconnect();
        }
        break;
      case 'pong':
        if (this.heartbeatTimeoutTimer != null) {
          clearTimeout(this.heartbeatTimeoutTimer);
          this.heartbeatTimeoutTimer = null;
        }
        break;
      default:
        break;
    }
  }

  private send(msg: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  // ─────────────────────────────────────────── timers

  /**
   * Handshake-stuck guard. If the socket stays in CONNECTING past the cap,
   * force-close it so `onSocketClose` runs and schedules a reconnect.
   */
  private armConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimeoutTimer = setTimeout(() => {
      this.connectTimeoutTimer = null;
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        console.warn('[chat-ws] connect handshake timed out — force-close');
        try {
          this.ws.close();
        } catch {
          // ignore — onSocketClose handles the rest.
        }
      }
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutTimer != null) {
      clearTimeout(this.connectTimeoutTimer);
      this.connectTimeoutTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      this.send({ type: 'ping' });
      if (this.heartbeatTimeoutTimer != null) {
        clearTimeout(this.heartbeatTimeoutTimer);
      }
      this.heartbeatTimeoutTimer = setTimeout(() => {
        console.warn('[chat-ws] heartbeat timeout — forcing reconnect');
        try {
          this.ws?.close();
        } catch {
          // ignore — onSocketClose handles the rest.
        }
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer != null) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  /**
   * Arm an in-band token refresh ahead of `tokenExp` (epoch ms, from the
   * `auth.ack` / `auth.refresh.ack`). Sending `auth.refresh` keeps the
   * socket alive across token lifetimes instead of letting the gateway
   * terminate it at expiry and forcing a full reconnect.
   */
  private scheduleAuthRefresh(tokenExp?: number): void {
    this.clearAuthRefreshTimer();
    if (typeof tokenExp !== 'number') return;
    const delay = Math.max(0, tokenExp - Date.now() - TOKEN_REFRESH_WINDOW_MS);
    this.authRefreshTimer = setTimeout(() => {
      this.authRefreshTimer = null;
      void this.sendAuthRefresh();
    }, delay);
  }

  private clearAuthRefreshTimer(): void {
    if (this.authRefreshTimer != null) {
      clearTimeout(this.authRefreshTimer);
      this.authRefreshTimer = null;
    }
  }

  private async sendAuthRefresh(): Promise<void> {
    const token = await this.getFreshToken();
    // Only refresh on a live, authenticated connection. If the socket
    // flapped during the token fetch, the new one re-auths from scratch,
    // so there's nothing to refresh here.
    if (!this.isConnected) return;
    if (!token) {
      this.scheduleReconnect();
      return;
    }
    this.send({ type: 'auth.refresh', data: { token } });
  }

  /** Stop for good: the store has no chat until it is switched back on. */
  private refuse(refusal: ChatRefusal): void {
    console.warn('[chat-ws] refused:', refusal);
    this.refusal$.next(refusal);
    this.tearDownWebsocketConnection();
    this.connectionState$.next('disconnected');
  }

  private scheduleReconnect(): void {
    // Always start from a clean slate: tear down the current socket (and
    // any pending reconnect timer) before arming the next attempt. This is
    // why callers can just "scheduleReconnect" regardless of socket state.
    this.tearDownWebsocketConnection();
    if (!this.alive || !this.appActive || this.refusal$.value != null) return;

    const baseDelay =
      RECONNECT_DELAYS[
        Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)
      ];
    const delay = withJitter(baseDelay);
    this.reconnectAttempt++;

    // State stays 'disconnected' (from teardown) during the backoff wait;
    // doConnect → initWebSocketConnection flips it to 'reconnecting' once
    // the attempt actually begins.
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.alive && this.appActive && !this.isConnected) {
        void this.doConnect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
